import { useEffect, useState } from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';

import { Card } from '@/components';
import { useAuth } from '@/contexts';
import { ApiError, authApi } from '@/lib';

// Strip trailing punctuation that email/messaging clients may append to URLs
function sanitizePage(page: string | undefined): string | undefined {
  return page?.replace(/[."']+$/, '');
}

function Login() {
  console.log('Login component rendering');
  const { login, isAuthenticated, isLoading, error, authedCode, authedPage } = useAuth();
  const { code, page: rawPage } = useParams<{ code: string; page: string }>();
  const page = sanitizePage(rawPage);
  console.log('code', code);
  console.log('page', page);
  const location = useLocation();
  const [loginError, setLoginError] = useState<string | null>(null);
  const [isValidatingCode, setIsValidatingCode] = useState(false);
  const [hasAttemptedValidation, setHasAttemptedValidation] = useState(false);

  // Reset validation guard when code or page changes so new params trigger validation
  useEffect(() => {
    setHasAttemptedValidation(false);
  }, [code, page]);

  useEffect(() => {
    // BES-7770: validate a direct /:code/:page link unless the cached session was already
    // minted from this exact code+page. The link supersedes the cached token, so a different
    // reservation's link (or same code, different page) can't reuse a stale/narrow token;
    // re-opening the same link skips the round-trip and so can't be stranded by a transient
    // validate failure.
    const sessionMatches = code === authedCode && page === authedPage;
    if (code && page && !sessionMatches && !isValidatingCode && !hasAttemptedValidation) {
      setIsValidatingCode(true);
      setHasAttemptedValidation(true);
      setLoginError(null);

      authApi
        .validateCode(code, page)
        .then((response) => {
          login(response.token, code, page);
        })
        .catch((err) => {
          const errorMessage = err instanceof ApiError ? err.message : 'Failed to validate access code';
          setLoginError(errorMessage);
        })
        .finally(() => {
          setIsValidatingCode(false);
        });
    }
  }, [code, page, authedCode, authedPage, login, isValidatingCode, hasAttemptedValidation]);

  useEffect(() => {
    if (error) {
      setLoginError(error);
    }
  }, [error]);

  // Redirect if already authenticated (after hooks to keep hook order stable).
  // BES-7770: a direct /:code/:page link supersedes the cached token. Redirect only when
  // the cached session was minted from THIS exact code+page, so a token from another
  // reservation or a narrow-scoped token from a different page is never reused.
  // Code-less routes redirect as before.
  const hasDirectCode = Boolean(code && page);
  const sessionMatchesLink = hasDirectCode && code === authedCode && page === authedPage;

  if (isAuthenticated && (!hasDirectCode || sessionMatchesLink)) {
    // Direct links (/:code/:page) always route to standalone pages. Portal
    // navigation is only entered via the dedicated /portal login flow.
    // Stripe callbacks also land on standalone routes — the result components
    // use location.pathname to decide portal vs standalone navigation.
    const getRedirectPath = (page: string | undefined): string => {
      const basePath = (() => {
        switch (page) {
          case 'rental-agreement':
            return '/sign-agreement';
          case 'damage-waiver':
            return '/damage-waiver';
          case 'damage-waiver-result':
            return '/damage-waiver-result';
          case 'check-in':
            return '/check-in';
          case 'pre-arrival-checklist':
            return '/pre-arrival-checklist';
          case 'guidebook':
            return '/guidebook';
          case 'add-ons':
            return '/add-ons';
          case 'add-on-result':
            return '/add-on-result';
          case 'portal':
            return '/portal/home';
          default:
            return location.state?.from?.pathname || '/';
        }
      })();

      // For payment result pages, preserve the query parameters (payment result data)
      if ((page === 'damage-waiver-result' || page === 'add-on-result') && location.search) {
        console.log('Forwarding payment result params:', location.search);
        return `${basePath}${location.search}`;
      }

      return basePath;
    };

    const redirectTo = getRedirectPath(page);
    return <Navigate to={redirectTo} replace />;
  }

  if (isLoading || isValidatingCode) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card header={<h2 className="text-lg font-semibold text-gray-900">Authenticating</h2>}>
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p>{isValidatingCode ? 'Validating your access code...' : 'Verifying your access token...'}</p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full space-y-8 p-8">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Guest Portal Access</h1>
          <p className="text-gray-600">Please use the link provided to access your reservation details.</p>
        </div>

        {code && page ? (
          <Card header={<h2 className="text-lg font-semibold text-gray-900">Access Link Detected</h2>}>
            <div className="text-center space-y-4">
              <p className="text-gray-600">
                We're validating your access to{' '}
                <strong>
                  {page === 'rental-agreement'
                    ? 'Rental Agreement'
                    : page === 'add-ons' || page === 'add-on-result'
                      ? 'Add-Ons'
                      : page === 'check-in'
                        ? 'Check-in Form'
                        : page === 'guidebook'
                          ? 'Guidebook'
                          : page === 'pre-arrival-checklist'
                            ? 'Pre-arrival Checklist'
                            : 'Damage Waiver'}
                </strong>
                . If this doesn't work automatically, please contact your host.
              </p>
              {loginError && (
                <div className="bg-red-50 border border-red-200 rounded-md p-3">
                  <p className="text-red-800 text-sm">{loginError}</p>
                </div>
              )}
            </div>
          </Card>
        ) : (
          <Card header={<h2 className="text-lg font-semibold text-gray-900">Invalid Access Link</h2>}>
            <div className="text-center space-y-4">
              <p className="text-gray-600">
                Please use the direct access link provided to you. The link should include both your access code and the
                specific document you need to access.
              </p>
              <p className="text-sm text-gray-500">
                Expected format: /[your-code]/rental-agreement, /[your-code]/damage-waiver, or
                /[your-code]/damage-waiver-result
              </p>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}

export default Login;
